import styled from "styled-components";

import type { VideoCallMode } from "../types";

interface RoomContainerProps {
  $minimised: boolean;
  $mode: VideoCallMode;
}

const RoomContainer = styled.div<RoomContainerProps>`
  background-color: ${props => props.theme.colors.greys.darker};
  border-radius: ${props => `${props.theme.borderRadius.basic} 0 0 ${props.theme.borderRadius.basic}`};
  bottom: ${props => props.$minimised && "0"};
  box-shadow: 0px 10px 20px rgba(141, 155, 173, 0.3);
  display: flex;
  height: ${props => (props.$minimised ? "320px" : "640px")};
  max-height: calc(100% - 70px);
  position: fixed;
  right: 0;
  top: ${props => !props.$minimised && "50px"};
  width: ${props =>
    props.$minimised ? `${props.theme.videoCallWidthMinimised}px` : `${props.theme.videoCallWidthMaximised}px`};
  max-width: 100vw;
  z-index: ${props => props.theme.zIndex.videoCall};

  ${props =>
    props.$mode === "floating" &&
    `
    border-radius: ${props.theme.borderRadius.basic};
  `}

  ${props => props.theme.belowBreakpoint} {
    border-radius: 0;
    max-height: 100%;
    height: 100%;
    top: 0;
    width: 100%;
    z-index: ${props => props.theme.zIndex.videoCallMobile};
  }
`;

const IdConfirmation = styled.div`
  color: ${props => props.theme.colors.white};
  left: ${props => props.theme.spacing.S_20};
  max-width: calc(100% - 160px);
  position: absolute;
  top: ${props => props.theme.spacing.S_20};
  width: 100%;
  ${props => props.theme.font.body1};
`;

const TopGradient = styled.div`
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.631042) 0%, rgba(0, 0, 0, 9e-5) 100%);
  border-top-left-radius: 8px;
  height: 84px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
`;

const BottomGradient = styled.div`
  background: linear-gradient(180deg, rgba(154, 154, 154, 0.0001) 0%, rgba(0, 0, 0, 0.7) 100%);
  border-bottom-left-radius: 8px;
  bottom: 0;
  height: 154px;
  left: 0;
  position: absolute;
  right: 0;
  width: 100%;
`;

const StatusText = styled.p`
  ${props => props.theme.font.header1};
  color: ${props => props.theme.colors.white};
  margin: 0;
  position: absolute;
  text-align: center;
  text-shadow: 0px 0px 10px ${props => props.theme.colors.black};
  top: calc(50% - 18px);
  width: 100%;
`;

const DragBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 24px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0) 100%);
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;

  &::after {
    content: "";
    width: 32px;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.5);
  }

  ${props => props.theme.belowBreakpoint} {
    display: none;
  }
`;

const ResizeHandle = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 24px;
  height: 24px;
  cursor: nwse-resize;
  z-index: 2;
  touch-action: none;

  &::before {
    content: "";
    position: absolute;
    bottom: 3px;
    right: 3px;
    width: 16px;
    height: 16px;
    border-right: 2px solid rgba(255, 255, 255, 0.7);
    border-bottom: 2px solid rgba(255, 255, 255, 0.7);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 7px;
    right: 7px;
    width: 8px;
    height: 8px;
    border-right: 2px solid rgba(255, 255, 255, 0.5);
    border-bottom: 2px solid rgba(255, 255, 255, 0.5);
  }

  ${props => props.theme.belowBreakpoint} {
    display: none;
  }
`;

export { RoomContainer, IdConfirmation, TopGradient, BottomGradient, StatusText, DragBar, ResizeHandle };
